import ElakayImage from '../../../components/images/ElakayImage';
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toImageUrl } from "../../../config";
import { getCardGlobalPresentation } from "../../../utils/appearanceSettings";
import { DropshippingLanguageContext } from "../i18n";
import { elakayAutoText } from "../../pageInside/dataElakay";
import GlobalRatingBadge from "../components/GlobalRatingBadge";
import "../../pageInside/Card.css";
import "./LinkedProviderProductCard.css";

const usd = (value) => (Number(value) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const MODE_KEY = { PROVIDER_DROPSHIPPING: "modeDs", PROVIDER_STOCK_SELLER_OWNED: "modeStore", SELLER_STOCK: "modeShip" };

const formatLastSynced = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

// "My Linked Provider Products" grid card (ticket §3/§4/§5). Reuses the same card presentation
// primitives as ProviderProductCard (elakay-grid-card + getCardGlobalPresentation + Card.css) so
// it matches Elakay's real design system, but its own data/actions -- View in My Shop /
// Resynchronize / Buy More Stock / Unlink -- since this is a DIFFERENT card (what the Seller
// already linked), not the Provider marketplace browsing card. Image/name prefer the Seller's OWN
// product record (link.sellerProduct) over the Provider's raw copy -- the Seller's title/image can
// differ from the Provider's, and that's the actual product in their shop.
const Thumb = ({ src, alt }) => {
  const [broken, setBroken] = useState(false);
  return (
    <div className="linked-product-card__media">
      {src && !broken ? (
        <ElakayImage preset="product-card" src={toImageUrl(src)} alt={alt || ""} loading="lazy" onError={() => setBroken(true)} />
      ) : (
        <div className="linked-product-card__no-image" aria-hidden="true" />
      )}
    </div>
  );
};

export default function LinkedProviderProductCard({ link, ct, onResync, onUnlink, onRestore, unlinking, restoring, resyncing, message, report }) {
  const infoSource = React.useContext(DropshippingLanguageContext);
  const presentation = getCardGlobalPresentation(infoSource?.allSettings);
  const provider = link.providerProductId;
  const sellerProduct = link.sellerProduct;
  const isDeletedRestorable = link.sellerProductState?.isDeleted === true;
  const active = sellerProduct?.sellActive?.sell === true;
  const unavailable = provider && (provider.active === false || (provider.status && provider.status !== "active"));
  const dropshippingOff = provider && provider.dropshippingEnabled === false;
  const name = sellerProduct?.name || provider?.name || elakayAutoText("h_lppc_unavailable", "(product unavailable)");
  const image = sellerProduct?.ProductImageUrls?.[0] || provider?.images?.[0];
  const economics = link.economics || {};

  if (isDeletedRestorable) {
    return (
      <div className={`product-card elakay-grid-card linked-product-card linked-product-card--restorable ${presentation.className || ""}`} style={{ ...presentation.style, ...presentation.variables }}>
        <Thumb src={provider?.images?.[0]} alt={name} />
        <div className="linked-product-card__body">
          <h3>{name}</h3>
          <p className="linked-product-card__meta">{elakayAutoText("h_lppc_prev_added", "Product previously added")}</p>
        </div>
        <div className="linked-product-card__actions">
          {link.sellerProductState?.canBeRestored !== false ? (
            <button type="button" className="linked-product-card__btn linked-product-card__btn--primary" disabled={restoring} onClick={() => onRestore(link._id)}>
              {restoring ? elakayAutoText("h_lppc_restoring", "Restoring…") : elakayAutoText("h_lppc_restore", "Restore to My Shop")}
            </button>
          ) : (
            <p className="linked-product-card__meta linked-product-card__meta--warn">{elakayAutoText("h_lppc_cant_restore", "This product cannot be restored.")}</p>
          )}
        </div>
        {message && <p className={`linked-product-card__message linked-product-card__message--${message.type}`}>{message.text}</p>}
      </div>
    );
  }

  return (
    <div className={`product-card elakay-grid-card linked-product-card ${presentation.className || ""}`} style={{ ...presentation.style, ...presentation.variables }} data-testid="linked-product-card">
      <Thumb src={image} alt={name} />
      <div className="linked-product-card__body">
        <h3>{name}</h3>
        {provider?.shop?.shopName && <p className="linked-product-card__provider">{provider.shop.shopName}</p>}
        <GlobalRatingBadge ratings={provider?.ratings} size="13px" />

        <dl className="linked-product-card__prices">
          <div><dt>{elakayAutoText("h_lppc_your_price", "Your Price")}</dt><dd data-testid="card-your-price">{usd(sellerProduct?.price)}</dd></div>
          <div><dt>{elakayAutoText("h_lppc_provider_cost", "Provider Cost")}</dt><dd>{usd(link.providerBasePrice)}</dd></div>
          <div><dt>{elakayAutoText("h_lppc_gross_margin", "Gross Margin")}</dt><dd>{usd(economics.grossMargin)}</dd></div>
          <div className={economics.netProfit == null ? "" : economics.netProfit > 0 ? "linked-product-card__net--positive" : "linked-product-card__net--negative"}>
            <dt>{elakayAutoText("h_lppc_net_profit", "Est. Net Profit")}</dt>
            <dd data-testid="card-net-profit">{economics.netProfit == null ? "—" : usd(economics.netProfit)}</dd>
          </div>
        </dl>

        <div className="linked-product-card__tags">
          <span className={`linked-product-card__status ${active ? "linked-product-card__status--active" : "linked-product-card__status--inactive"}`}>
            {active ? `✓ ${elakayAutoText("h_lppc_active", "Active")}` : elakayAutoText("h_lppc_inactive", "Inactive")}
          </span>
          <span className="linked-product-card__fulfillment">{ct(MODE_KEY[link.fulfillmentMode] || "modeDs")}</span>
        </div>

        {link.myStock?.purchased > 0 && (
          <p className="linked-product-card__meta" data-testid="card-stock">
            {ct("rowStock")}: {ct("colAvailable")} {link.myStock.available} · {ct("colReserved")} {link.myStock.reserved} · {ct("inTransit")} {link.myStock.inTransit}
          </p>
        )}
        {link.lastSyncedAt && <p className="linked-product-card__meta">{elakayAutoText("h_lppc_last_sync", "Last synchronized")}: {formatLastSynced(link.lastSyncedAt)}</p>}
        {link.needsReview && <p className="linked-product-card__meta linked-product-card__meta--warn">{elakayAutoText("h_lppc_needs_review", "Provider product changed — some filters may need a review.")}</p>}
        {unavailable && <p className="linked-product-card__meta linked-product-card__meta--warn">{elakayAutoText("h_lppc_provider_unavailable", "Provider product unavailable.")}</p>}
        {!unavailable && dropshippingOff && <p className="linked-product-card__meta linked-product-card__meta--warn">{elakayAutoText("h_lppc_ds_off", "Dropshipping unavailable for this product.")}</p>}
      </div>

      <div className="linked-product-card__actions">
        <Link to={`/product/edit/${link.sellerProductId}`} className="linked-product-card__btn linked-product-card__btn--ghost">
          {elakayAutoText("h_lppc_view_shop", "View in My Shop")}
        </Link>
        <button type="button" className="linked-product-card__btn linked-product-card__btn--primary" disabled={resyncing} onClick={() => onResync(link)}>
          {resyncing ? ct("resyncing") : ct("resyncBtn")}
        </button>
        {provider?._id && link.terms?.purchase?.enabled && (
          <Link to={`/seller/provider-products/${provider._id}`} className="linked-product-card__btn linked-product-card__btn--ghost">{ct("buyMore")}</Link>
        )}
        <button type="button" className="linked-product-card__btn linked-product-card__btn--danger" disabled={unlinking} onClick={() => onUnlink(link._id)}>
          {unlinking ? ct("dsi_unlinking") : ct("dsi_unlinkBtn")}
        </button>
      </div>

      {message && <p className={`linked-product-card__message linked-product-card__message--${message.type}`}>{message.text}</p>}
      {report && (
        <div className="linked-product-card__report" data-testid="sync-report">
          <strong>{ct("reportTitle")}</strong>
          <ul>
            <li>{ct("repImages").replace("{value0}", report.images.count)}</li>
            <li>{ct("repCategories").replace("{value0}", report.categories)}</li>
            <li>{ct("repFilters").replace("{value0}", report.filters.selected)} · {ct("repVariants").replace("{value0}", report.variants)}</li>
          </ul>
        </div>
      )}
    </div>
  );
}
